/**
 * i18n Configuration for AmalQ
 *
 * Initializes react-i18next with:
 * - Browser language detection (OS/browser settings)
 * - Lazy-loaded translation namespaces via dynamic imports
 * - RTL direction management (Arabic, Urdu = RTL; English, Turkish, Roman Urdu = LTR)
 * - localStorage persistence of language preference
 *
 * Supported languages:
 *   English (en)  -- LTR, default
 *   Arabic  (ar)  -- RTL, Modern Standard Arabic (الفصحى)
 *   Turkish (tr)  -- LTR, formal register (siz) with warm tone
 *   Urdu    (ur)  -- RTL, Nastaliq script, conversational Pakistani Urdu
 *   Roman Urdu (ur-Latn) -- LTR, Latin script, same conversational tone
 *   Spanish (es)  -- LTR, tuteo register, neutral international Spanish
 *   Bengali (bn)  -- LTR, Cholti Bhasha register, apni formality
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';

// Locale list is generated at build time from the backend registry.
// See frontend/scripts/sync-locales.js — DO NOT add a hand-written list here.
import {
  DEFAULT_LOCALE,
  RTL_LOCALES,
  SUPPORTED_LOCALES,
  type UiLocale,
} from './locales.generated';

export { DEFAULT_LOCALE, SUPPORTED_LOCALES };

/** Re-exported from the generated registry so existing imports keep working. */
export type SupportedLocale = UiLocale;

/** Translation namespaces -- each maps to a JSON file in locales/{lang}/ */
export const NAMESPACES = [
  'common',
  'auth',
  'campaigns',
  'donations',
  'dashboard',
  'admin',
  'landing',
  'contact',
  'whyExists',
  'legal',
  'legalPolicies',
  'legalCommerce',
  'help',
  'blog',
  'gazian',
  'seo',
  'launchpad',
  'notifications',
  'startCampaign',
  'aboutUs',
  'fundasister',
  'bangrilla',
  'ashrafie',
  'listings',
  'prayer',
] as const;
export type TranslationNamespace = (typeof NAMESPACES)[number];

/** Default namespace used when no namespace is specified */
export const DEFAULT_NS: TranslationNamespace = 'common';

/**
 * Check if a locale uses RTL direction. Reads the registry-generated set so
 * adding a new RTL language requires only a registry edit + codegen, no source
 * changes here.
 */
export function isRTL(locale: string): boolean {
  return RTL_LOCALES.has(locale as UiLocale);
}

/**
 * Get text direction for a locale
 */
export function getDirection(locale: string): 'rtl' | 'ltr' {
  return isRTL(locale) ? 'rtl' : 'ltr';
}

/**
 * Update <html> element attributes for current locale.
 * Sets `dir` (text direction) and `lang` (language code).
 */
export function applyLocaleToDocument(locale: string): void {
  const dir = getDirection(locale);
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', locale);
}

/**
 * Extract locale from a URL pathname.
 * Returns the locale if the path starts with a supported locale prefix, otherwise null.
 *
 * @example
 * getLocaleFromPath('/ar/campaigns') // 'ar'
 * getLocaleFromPath('/en/blog/post') // 'en'
 * getLocaleFromPath('/campaigns')    // null
 */
export function getLocaleFromPath(pathname: string): SupportedLocale | null {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  if (firstSegment && SUPPORTED_LOCALES.includes(firstSegment as SupportedLocale)) {
    return firstSegment as SupportedLocale;
  }
  return null;
}

/**
 * Remove the locale prefix from a pathname.
 *
 * @example
 * stripLocaleFromPath('/ar/campaigns') // '/campaigns'
 * stripLocaleFromPath('/en/')          // '/'
 * stripLocaleFromPath('/campaigns')    // '/campaigns'
 */
export function stripLocaleFromPath(pathname: string): string {
  const locale = getLocaleFromPath(pathname);
  if (!locale) return pathname;
  const stripped = pathname.replace(new RegExp(`^/${locale}`), '') || '/';
  return stripped;
}

/**
 * Prepend the locale prefix to a pathname.
 *
 * @example
 * addLocaleToPath('/campaigns', 'ar') // '/ar/campaigns'
 * addLocaleToPath('/', 'en')          // '/en'
 */
export function addLocaleToPath(pathname: string, locale: string): string {
  // Don't add locale prefix to admin, gazian, or asset paths
  if (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/assets') ||
    pathname.startsWith('/fonts') ||
    pathname.startsWith('/logo') ||
    pathname.startsWith('/images')
  ) {
    return pathname;
  }

  // Strip existing locale prefix if present
  const stripped = stripLocaleFromPath(pathname);
  const cleanPath = stripped === '/' ? '' : stripped;
  return `/${locale}${cleanPath}`;
}

// Initialize i18next
i18n
  // Load translation files dynamically via Vite's import()
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`../../locales/${language}/${namespace}.json`)
    )
  )
  // Detect user language from browser/OS settings
  .use(LanguageDetector)
  // Connect to React
  .use(initReactI18next)
  .init({
    // Language settings — read-only registry-generated tuple
    supportedLngs: [...SUPPORTED_LOCALES],
    // Each locale falls back directly to English.
    // ur-Latn does NOT fall back to ur (different script would confuse users).
    fallbackLng: {
      'ur-Latn': ['en'],
      'ur': ['en'],
      'bn': ['en'],
      default: ['en'],
    },
    defaultNS: DEFAULT_NS,
    ns: [DEFAULT_NS],

    // Detection settings -- priority cascade
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0,
      lookupLocalStorage: 'amalq-lang',
      caches: ['localStorage'],
    },

    // Interpolation (React already escapes)
    interpolation: {
      escapeValue: false,
    },

    // React-specific settings
    react: {
      useSuspense: true,
    },

    // Namespace loading -- load 'common' eagerly, others on demand
    partialBundledLanguages: true,
  });

// Apply document direction whenever language changes
i18n.on('languageChanged', (lng: string) => {
  applyLocaleToDocument(lng);
  // Persist preference
  try {
    localStorage.setItem('amalq-lang', lng);
  } catch {
    // localStorage unavailable -- silent fail
  }
});

// Apply initial direction on load
if (i18n.language) {
  applyLocaleToDocument(i18n.language);
}

export default i18n;
